// @ts-nocheck - formatter / Table
import React, { useState } from 'react';
import {
  loadSoldTransactions,
  exportSoldTransactionData,
} from '../../../actions/keyCodeActions';
import { Link, useLocation } from 'react-router-dom';
import { Table, SimpleTextFilter, getVisibleColumns } from '../../common/Table';
import { CommaNumber_NoDecimal } from '../../../util/formatter';
import FormattedDate from '../../common/FormattedDate';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings';
import TransactionStatusBadge from './TransactionStatusBadge';
import KeyProviderLink from '../../common/links/KeyProviderLink';
import UserLink from '../../common/links/UserLink';
import { useTranslation } from 'react-i18next';

export interface ReportedSoldListProps {
  reload?: any;
}

function ReportedSoldList({ reload }: ReportedSoldListProps) {
  const location = useLocation();
  const allColumns = [
    'creationDate',
    'batchCount',
    'keyProviderName',
    'manualImportUser',
    'status',
    'description',
  ];
  const defaultVisibleColumns = allColumns;
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState<any>();
  const params = new URLSearchParams(location.search);
  const { tableSettings, handleSetTableSettings: setTableSettings, filters, filterChange, isLoading } =
    useTableSettings(
      'reportedsoldlist-settings',
      getTableSettingsDefaults({
        defaultSort: 'creationDate',
        direction: 'desc',
        defaultVisibleColumns,
      }),
      null,
      loadSoldTransactions,
      setTransactions,
      reload,
      t('Failed to load sold transactions')
    );

  let columnsConfig = [
    {
      Key: 'creationDate',
      Label: t('Uploaded date'),
      width: '15%',
      Render: (item: any) => (
        <Link to={'/keys/reported-sold/' + item.id}>
          <FormattedDate value={item.creationDate} variant="default" />
        </Link>
      ),
    },
    {
      Key: 'batchCount',
      Label: t('Key count'),
      width: '5%',
      className: 'text-right num no-wrap',
      Render: (item: any) =>
        item.batchCount >= 0 ? CommaNumber_NoDecimal(item.batchCount) : '--',
    },
    {
      Key: 'keyProviderName',
      Label: t('Provider'),
      width: '15%',
      Render: (item: any) => (
        <KeyProviderLink id={item.keyProviderId} name={item.keyProviderName} />
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'manualImportUser',
      Label: t('Uploaded by'),
      width: '15%',
      Render: (item: any) => (
        <>
          <UserLink id={item.manualImportUserId} name={item.manualImportUser} />
          <br />
          {item.manualImportUserCompany}
        </>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'status',
      Label: t('Status'),
      width: '5%',
      Render: (item: any) => (
        <TransactionStatusBadge
          success={item.success}
          partialSuccess={item.partialSuccess}
          message={item.message}
          status={item.status}
          isAuthorised={item.isAuthorised}
        />
      ),
      Filter: {
        Render: (col: any) => {
          const value = col.filterValue ?? params.get(`f_${col.Key}`) ?? '';
          return (
            <select
              className={`form-control ${value ? 'filtered' : ''}`}
              value={value}
              onChange={(el) => filterChange(col.Key, (el.target as HTMLSelectElement).value)}
            >
              <option value="">{t('All')}</option>
              <option value="failed">{t('Failed')}</option>
              <option value="partial success">{t('Partial success')}</option>
              <option value="pending authorisation">{t('Pending authorisation')}</option>
              <option value="pending">{t('Pending')}</option>
              <option value="success">{t('Success')}</option>
            </select>
          );
        },
      },
    },
    {
      Key: 'description',
      Label: t('Description'),
      width: '25%',
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
  ];

  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  return transactions ? (
    <KwPanel className="mb-4">
      <Table
        columns={columns}
        allColumns={allColumns}
        rows={transactions.data.map((x: any) => ({ Key: x.id, ...x }))}
        totalRowCount={transactions.totalRowCount}
        filteredRowCount={transactions.filteredRowCount}
        scopes={transactions.scopes}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filters={filters}
        filterChange={filterChange}
        exportData={exportSoldTransactionData}
      />
    </KwPanel>
  ) : (
    <Loading />
  );
}

export default ReportedSoldList;
